import { Link, Route, Routes } from "react-router-dom";
import { ProtectedRoute } from "./auth/ProtectedRoute.js";
import { Onboarding } from "./onboarding/Onboarding.js";
import { ConfirmSignUp } from "./routes/ConfirmSignUp.js";
import { Opportunity } from "./routes/Opportunity.js";
import { OpportunityFeedback } from "./routes/OpportunityFeedback.js";
import { PortalApply } from "./routes/PortalApply.js";
import { PortalInbox } from "./routes/PortalInbox.js";
import { PortalOpportunities } from "./routes/PortalOpportunities.js";
import { PortalOpportunityDetail } from "./routes/PortalOpportunityDetail.js";
import { PortalProfile } from "./routes/PortalProfile.js";
import { PortalQuotes } from "./routes/PortalQuotes.js";
import { PortalShell } from "./routes/PortalShell.js";
import { SignIn } from "./routes/SignIn.js";
import { SignUp } from "./routes/SignUp.js";
import { BrandLogo } from "./ui/BrandLogo.js";

/**
 * Route map for the Pro Portal.
 *
 * Two authentication worlds, deliberately separate (build-plan units 9/15):
 *   - /opportunities/:invitationId — the magic-link page from invitation
 *     emails. Token-in-fragment auth, NO sign-in, must keep working for
 *     Group 1 contractors who never create an account.
 *   - /portal/* — the signed-in portal (Slice 3). Cognito-JWT auth via
 *     <ProtectedRoute>; unclaimed accounts get the application-pending
 *     experience inside the shell.
 */
export function App() {
  return (
    <Routes>
      <Route path="/" element={<Landing />} />
      <Route path="/opportunities/:invitationId" element={<Opportunity />} />
      {/* Post-job feedback from the emailed link. Two paths, one form: the
          :preset segment carries the star tapped in the email. */}
      <Route path="/opportunities/:invitationId/feedback" element={<OpportunityFeedback />} />
      <Route
        path="/opportunities/:invitationId/feedback/:preset"
        element={<OpportunityFeedback />}
      />
      <Route path="/start" element={<Onboarding />} />
      <Route path="/sign-in" element={<SignIn />} />
      <Route path="/sign-up" element={<SignUp />} />
      <Route path="/confirm" element={<ConfirmSignUp />} />
      <Route
        path="/portal"
        element={
          <ProtectedRoute>
            <PortalShell />
          </ProtectedRoute>
        }
      >
        <Route index element={<PortalOpportunities />} />
        <Route path="opportunities/:invitationId" element={<PortalOpportunityDetail />} />
        <Route path="quotes" element={<PortalQuotes />} />
        <Route path="inbox" element={<PortalInbox />} />
        <Route path="apply" element={<PortalApply />} />
        <Route path="profile" element={<PortalProfile />} />
      </Route>
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

function Landing() {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-6 px-6 py-12">
      <header className="flex flex-col gap-4">
        <BrandLogo />
        <h1 className="text-3xl font-bold text-charcoal">Pro Portal</h1>
      </header>
      <p className="text-base leading-relaxed text-charcoal">
        Get matched with local home &amp; business jobs. Start with your website and we&rsquo;ll
        build your pro profile for you — you just review it.
      </p>
      <div className="flex flex-col gap-3">
        <Link
          to="/start"
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl bg-brand-700 px-6 text-base font-semibold text-white shadow-sm active:bg-brand-800"
        >
          Get started
        </Link>
        <Link
          to="/sign-in"
          className="inline-flex min-h-[44px] w-full items-center justify-center rounded-xl border border-brand-700 px-6 text-base font-semibold text-brand-700 active:bg-brand-50"
        >
          Pro sign in
        </Link>
      </div>
      <p className="text-sm text-charcoal">
        Already get invitations by email? The link in your invitation brings you straight to the job
        — no account needed.
      </p>
    </main>
  );
}

function NotFound() {
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col justify-center gap-4 px-6 py-12">
      <BrandLogo className="mb-2" />
      <h1 className="text-2xl font-bold text-charcoal">Page not found</h1>
      <p className="text-base text-charcoal">
        If you followed a link from an invitation email, the invitation may have expired. Check the
        email for a newer link.
      </p>
    </main>
  );
}
